import React from "react";
import { useCreateMasteryWorkflowMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { LegacyTokens } from "../../../theme/b13Tokens";
import WorkflowModal from "../../Settings/Workflows/shared/WorkflowModal";
import WorkflowEditorBody, {
  EditorPhase, normalisePhase, accuracyValidation,
} from "../../Settings/Workflows/shared/WorkflowEditorBody";

// Batch 7 — replaces the "workflow" branch of QuickHierarchyDrawer.
// On save, onCreated returns the new workflow's id so the program editor
// can auto-select it in the Mastery Workflow field and stay put — every
// other form value survives because parent state was never unmounted.

const STARTER_PHASES: Omit<EditorPhase, "orderNumber" | "id">[] = [
  { phaseName: "Baseline",       colour: LegacyTokens.neutralDot, icon: "AssessmentOutlined", description: "Starting phase.", entryRule: "", advancementRule: "", regressionRule: "", transitionsTo: "Acquisition", masteredTargetStatus: "", notMasteredTransitionsTo: "",    minPercentCorrect: 0,   acrossSessions: 1, minTrialsPerSession: 3, promptRequirement: "any",              manualApprovalRequired: false, isStarting: true,  isTerminal: false, isActive: true, countsTowardAutoOpenLimit: true },
  { phaseName: "Acquisition",    colour: LegacyTokens.blue,       icon: "Layers",             description: "",                entryRule: "", advancementRule: "", regressionRule: "", transitionsTo: "Generalization", masteredTargetStatus: "", notMasteredTransitionsTo: "", minPercentCorrect: 80,  acrossSessions: 3, minTrialsPerSession: 5, promptRequirement: "any",              manualApprovalRequired: false, isStarting: false, isTerminal: false, isActive: true, countsTowardAutoOpenLimit: true },
  { phaseName: "Generalization", colour: LegacyTokens.amber,      icon: "AutoFixHigh",        description: "",                entryRule: "", advancementRule: "", regressionRule: "", transitionsTo: "Mastered", masteredTargetStatus: "", notMasteredTransitionsTo: "",       minPercentCorrect: 90,  acrossSessions: 2, minTrialsPerSession: 5, promptRequirement: "independent_only", manualApprovalRequired: false, isStarting: false, isTerminal: false, isActive: true, countsTowardAutoOpenLimit: true },
  { phaseName: "Mastered",       colour: LegacyTokens.emerald,    icon: "EmojiEvents",        description: "",                entryRule: "", advancementRule: "", regressionRule: "", transitionsTo: "Maintenance", masteredTargetStatus: "", notMasteredTransitionsTo: "",    minPercentCorrect: 100, acrossSessions: 1, minTrialsPerSession: 1, promptRequirement: "independent_only", manualApprovalRequired: true,  isStarting: false, isTerminal: true,  isActive: true, countsTowardAutoOpenLimit: false },
  { phaseName: "Maintenance",    colour: LegacyTokens.purple,     icon: "Repeat",             description: "",                entryRule: "", advancementRule: "", regressionRule: "", transitionsTo: "", masteredTargetStatus: "", notMasteredTransitionsTo: "",               minPercentCorrect: 80,  acrossSessions: 1, minTrialsPerSession: 3, promptRequirement: "independent_only", manualApprovalRequired: false, isStarting: false, isTerminal: true,  isActive: true, countsTowardAutoOpenLimit: false },
];

const starterPhases = (): EditorPhase[] =>
  STARTER_PHASES.map((p, i) => normalisePhase({ ...p, orderNumber: i }, i));

export default function WorkflowQuickModal(props: {
  open: boolean;
  onClose: () => void;
  onCreated: (kind: "hierarchy" | "workflow", id: number) => void;
}) {
  const { open, onClose, onCreated } = props;
  const { showMessage, showErrorMessage } = useNotifier();
  const [createWorkflow, { isLoading: saving }] = useCreateMasteryWorkflowMutation();

  const [workflowName, setWorkflowName] = React.useState("");
  const [objective, setObjective]       = React.useState("Increase Percent Correct");
  const [phases, setPhases]             = React.useState<EditorPhase[]>(starterPhases);
  const [dirty, setDirty]               = React.useState(false);
  const [validated, setValidated]       = React.useState(false);
  // Batch 17 / 7C §7C.5 — auto-open state. Quick-create defaults off;
  // the clinician can flip it on before saving if they know what they
  // want, but the field surface is intentionally minimal here (this
  // modal is the "just make me a workflow so I can continue" path).
  const [autoOpenEnabled, setAutoOpenEnabled] = React.useState(false);
  const [autoOpenMax, setAutoOpenMax]         = React.useState<number | null>(null);
  const [autoOpenInitIdx, setAutoOpenInitIdx] = React.useState<number | null>(null);
  const [autoOpenDir, setAutoOpenDir]         = React.useState<string | null>(null);

  const nameRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (open) {
      setWorkflowName("");
      setObjective("Increase Percent Correct");
      setPhases(starterPhases());
      setAutoOpenEnabled(false);
      setAutoOpenMax(null);
      setAutoOpenInitIdx(null);
      setAutoOpenDir(null);
      setDirty(false);
      setValidated(false);
    }
  }, [open]);

  const workflowNameError = React.useMemo<string | null>(() => {
    if (!validated) return null;
    if (!workflowName.trim()) return "Please enter a name for this workflow.";
    return null;
  }, [workflowName, validated]);

  const anyAccuracyError = React.useMemo(() => {
    if (!validated) return false;
    return phases.some((p) => accuracyValidation(p).error !== null);
  }, [phases, validated]);

  const disableSave = validated && (Boolean(workflowNameError) || anyAccuracyError);

  const focusFirstInvalid = () => {
    if (!workflowName.trim()) {
      nameRef.current?.focus();
      nameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const bad = phases.findIndex((p) => accuracyValidation(p).error !== null);
    if (bad >= 0) {
      const cards = document.querySelectorAll<HTMLElement>('[data-testid="phase-card"]');
      cards[bad]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const save = async () => {
    setValidated(true);
    if (!workflowName.trim() || phases.some((p) => accuracyValidation(p).error !== null)) {
      setTimeout(focusFirstInvalid, 0);
      return;
    }
    try {
      const res: any = await createWorkflow({
        workflowName: workflowName.trim(),
        objective,
        status: "active",
        isDefault: false,
        scope: "organization",
        automaticProgression: true,
        manualOverrideAllowed: true,
        supervisorApprovalRequired: false,
        phases: phases.map((p, i) => ({
          id: p.id, // Batch 18 — for interval preservation on future saves
          phaseName: p.phaseName.trim(),
          colour: p.colour,
          icon: p.icon,
          description: p.description || null,
          entryRule: p.entryRule || null,
          advancementRule: p.advancementRule || null,
          regressionRule: p.regressionRule || null,
          transitionsTo: p.transitionsTo || null,
          minPercentCorrect: p.minPercentCorrect,
          acrossSessions: p.acrossSessions,
          minTrialsPerSession: p.minTrialsPerSession,
          promptRequirement: p.promptRequirement,
          manualApprovalRequired: p.manualApprovalRequired,
          isStarting: p.isStarting,
          isTerminal: p.isTerminal,
          isActive: p.isActive,
          countsTowardAutoOpenLimit: p.countsTowardAutoOpenLimit,
          orderNumber: i,
        })),
        autoOpenEnabled,
        autoOpenMaxSimultaneous: autoOpenMax,
        autoOpenInitialPhaseIndex: autoOpenInitIdx != null && autoOpenInitIdx >= 0 ? autoOpenInitIdx : null,
        autoOpenDirection: autoOpenDir,
      }).unwrap();
      if (res?.status && res?.data?.id) {
        showMessage("Workflow saved.");
        setDirty(false);
        onCreated("workflow", Number(res.data.id));
        onClose();
      } else {
        showErrorMessage(res?.message || "Failed to save workflow.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Save failed.");
    }
  };

  return (
    <WorkflowModal
      open={open}
      title="Add workflow"
      onClose={onClose}
      onSave={save}
      onOpenInNewTab={() => window.open("/aba-therapy/workflows/new", "_blank", "noopener")}
      saving={saving}
      disableSave={disableSave}
      hasUnsavedChanges={dirty}
    >
      <WorkflowEditorBody
        workflowName={workflowName}
        onWorkflowNameChange={(v) => { setDirty(true); setWorkflowName(v); }}
        workflowNameError={workflowNameError}
        objective={objective}
        phases={phases}
        onPhasesChange={(u) => { setDirty(true); setPhases(u); }}
        autoOpenEnabled={autoOpenEnabled}
        autoOpenMaxSimultaneous={autoOpenMax}
        autoOpenInitialPhaseIndex={autoOpenInitIdx}
        autoOpenDirection={autoOpenDir}
        onAutoOpenChange={(patch) => {
          setDirty(true);
          if (patch.autoOpenEnabled !== undefined) setAutoOpenEnabled(patch.autoOpenEnabled);
          if (patch.autoOpenMaxSimultaneous !== undefined) setAutoOpenMax(patch.autoOpenMaxSimultaneous);
          if (patch.autoOpenInitialPhaseIndex !== undefined) setAutoOpenInitIdx(patch.autoOpenInitialPhaseIndex);
          if (patch.autoOpenDirection !== undefined) setAutoOpenDir(patch.autoOpenDirection);
        }}
        registerNameRef={(el) => { nameRef.current = el; }}
      />
    </WorkflowModal>
  );
}
